import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { getExamination } from "@/lib/api";
import BoundingBoxOverlay from "@/components/BoundingBoxOverlay";
import { CLASS_COLORS } from "@/components/InteractiveBoxEditor";

/**
 * Modal to preview an exam's X-ray with bounding boxes overlaid.
 * Supports prev/next navigation through a list of exam IDs.
 */
export default function ExamPreviewModal({
  examIds,
  currentIndex,
  onClose,
  onIndexChange,
}) {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);

  const id = examIds[currentIndex];

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setExam(null);
    getExamination(id)
      .then(setExam)
      .catch(() => setExam(null))
      .finally(() => setLoading(false));
  }, [id]);

  // Keyboard nav
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) onIndexChange(currentIndex - 1);
      if (e.key === "ArrowRight" && currentIndex < examIds.length - 1) onIndexChange(currentIndex + 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [currentIndex, examIds.length, onClose, onIndexChange]);

  const preds = exam?.predictions || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="exam-preview-modal"
    >
      <div
        className="w-full max-w-6xl rounded-2xl border border-zinc-800 bg-zinc-950 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Preview · {currentIndex + 1} / {examIds.length}
            </div>
            <div className="text-sm text-zinc-100 truncate font-mono">
              {exam?.patient?.patient_id || (loading ? "Loading..." : "-")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={currentIndex === 0}
              onClick={() => onIndexChange(currentIndex - 1)}
              data-testid="preview-prev"
              className="p-1.5 rounded border border-zinc-800 text-zinc-300 hover:bg-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              disabled={currentIndex >= examIds.length - 1}
              onClick={() => onIndexChange(currentIndex + 1)}
              data-testid="preview-next"
              className="p-1.5 rounded border border-zinc-800 text-zinc-300 hover:bg-zinc-900 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {exam && (
              <Link
                to={`/exam/${exam.id}`}
                data-testid="preview-open-full"
                className="inline-flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1.5 rounded border border-indigo-500/20 bg-indigo-500/10"
              >
                Buka Detail <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            <button
              onClick={onClose}
              data-testid="preview-close"
              className="p-1.5 rounded border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-0 max-h-[80vh]">
          {/* Image viewer */}
          <div className="p-4 bg-black flex items-center justify-center min-h-[400px]">
            {loading && (
              <div className="text-zinc-500 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Memuat gambar...
              </div>
            )}
            {!loading && exam && (
              <BoundingBoxOverlay
                imageSrc={exam.image_base64}
                predictions={preds}
                imageWidth={exam.image_width}
                imageHeight={exam.image_height}
                testId="preview-viewer"
              />
            )}
            {!loading && !exam && (
              <div className="text-rose-400 text-sm">Gagal memuat exam</div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="border-t lg:border-t-0 lg:border-l border-zinc-900 bg-zinc-900/40">
            <div className="p-4 border-b border-zinc-900">
              <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold mb-2">
                AI Findings
              </div>
              <div className="font-display text-xl font-medium">
                {preds.length}{" "}
                <span className="text-sm text-zinc-500 font-normal">
                  temuan
                </span>
              </div>
            </div>
            <div className="max-h-[400px] overflow-y-auto divide-y divide-zinc-900">
              {preds.length === 0 && !loading && (
                <div className="p-6 text-center text-sm text-emerald-400">
                  Tidak ditemukan abnormalitas
                </div>
              )}
              {preds.map((p, i) => {
                const conf = p.confidence ?? 0;
                const color = CLASS_COLORS[p.class] || "#818cf8";
                return (
                  <div key={i} className="px-4 py-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="w-1.5 h-6 rounded-sm shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="text-sm font-medium text-zinc-100 truncate">
                          {p.class}
                        </span>
                      </div>
                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">
                        {(conf * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 text-[10px] text-zinc-500 text-center border-t border-zinc-900">
              ← → untuk navigasi · Esc untuk tutup
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
